import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Box, Paper, Typography, Stack, Link } from '@mui/material';
import { TOURNAMENTS } from '../tournaments';
import { formatLocation } from '../utils/eventUtils';
import HowiesHometownCup2025Page from './tournaments/HowiesHometownCup2025Page';
import HowiesHometownCup2026Page from './tournaments/HowiesHometownCup2026Page';
import CcmWorldInvite2026Page from './tournaments/CcmWorldInvite2026Page';
import CcmMotownPage from './tournaments/CcmMotownPage';

const TournamentPage = () => {
    const { tournamentKey } = useParams<{ tournamentKey: string }>();
    const tournament = TOURNAMENTS.find(t => t.key === tournamentKey);

    useEffect(() => {
        if (tournament) {
            document.title = `Jets '14 | ${tournament.name}`;
        } else {
            document.title = "Jets '14 | Tournament";
        }
    }, [tournament]);

    if (!tournament) {
        return (
            <Container maxWidth="md">
                <Box sx={{ my: 4 }}>
                    <Typography variant="h4" component="h1">Tournament Not Found</Typography>
                </Box>
            </Container>
        );
    }

    const renderTournamentContent = () => {
        switch (tournamentKey) {
            case 'ccm-world-invite-2026':
                return <CcmWorldInvite2026Page />;
            case 'howies-hometown-cup-2026':
                return <HowiesHometownCup2026Page />;
            case 'howies-hometown-cup-2025':
                return <HowiesHometownCup2025Page />;
            case 'ccm-motown-2025':
                return <CcmMotownPage />;
            default:
                return <Box sx={{ mt: 2 }}>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        Details coming soon.
                    </Typography>
                    {tournament.url && (
                        <Link href={tournament.url} target="_blank" rel="noopener noreferrer">
                            View Tournament Website
                        </Link>
                    )}
                </Box>;
        }
    };

    return (
        <Container maxWidth="md" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
            <Box sx={{ my: { xs: 1.5, sm: 3, md: 4 } }}>
                <Paper
                    variant="outlined"
                    elevation={0}
                    sx={{
                        p: { xs: 1, sm: 3, md: 4 },
                        borderRadius: { xs: 0, sm: 2 },
                        border: { xs: 'none', sm: '1px solid' },
                        borderColor: 'divider',
                        bgcolor: { xs: 'transparent', sm: 'background.paper' }
                    }}
                >
                    <Stack
                        direction="row"
                        spacing={{ xs: 1.5, sm: 2 }}
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        sx={{ mb: { xs: 2, sm: 3 }, px: { xs: 0.5, sm: 0 } }}
                    >
                        {tournament.logo && (
                            <Box
                                component="img"
                                src={tournament.logo.startsWith('http') ? tournament.logo : process.env.PUBLIC_URL + tournament.logo}
                                alt={`${tournament.name} logo`}
                                sx={{ height: { xs: 44, sm: 60 }, width: 'auto', flexShrink: 0, mt: { xs: 0.5, sm: 0 } }}
                            />
                        )}
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                                variant="h4"
                                component="h1"
                                sx={{
                                    fontSize: { xs: '1.35rem', sm: '1.85rem', md: '2.1rem' },
                                    fontWeight: 700,
                                    lineHeight: 1.2,
                                    mb: 0.5
                                }}
                            >
                                {tournament.name}
                            </Typography>
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={{ xs: 0.25, sm: 1 }}
                                alignItems={{ xs: 'flex-start', sm: 'center' }}
                                divider={<Box component="span" sx={{ display: { xs: 'none', sm: 'inline' }, mx: 0.5, color: 'text.secondary' }}>•</Box>}
                            >
                                <Typography variant="subtitle1" color="text.secondary" sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}>
                                    {tournament.dates}
                                </Typography>
                                <Typography
                                    variant="subtitle1"
                                    color="text.secondary"
                                    sx={{
                                        fontSize: { xs: '0.85rem', sm: '1rem' },
                                        '& a': { color: 'inherit', textDecoration: 'none' },
                                        '& a:hover': { textDecoration: 'underline' }
                                    }}
                                >
                                    {formatLocation(tournament.location)}
                                </Typography>
                            </Stack>
                        </Box>
                    </Stack>
                    {renderTournamentContent()}
                </Paper>
            </Box>
        </Container>
    );
};

export default TournamentPage;